import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-29cdde1a"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "ymd_form" }
const _hoisted_2 = { class: "ymd_form_year" }
const _hoisted_3 = { class: "ymd_form_year_select" }
const _hoisted_4 = { class: "ymd_form_year_suffix" }
const _hoisted_5 = { class: "ymd_form_month" }
const _hoisted_6 = { class: "ymd_form_month_select" }
const _hoisted_7 = { class: "ymd_form_month_suffix" }
const _hoisted_8 = { class: "ymd_form_day" }
const _hoisted_9 = { class: "ymd_form_day_select" }
const _hoisted_10 = { class: "ymd_form_day_suffix" }

import { PropType, computed, ref, Ref, watchEffect, toRefs, watch } from 'vue';
import { some } from 'lodash';
import BaseSelectBox, { Options } from '@/components/BaseSelectBox.vue';
import { fifteenYearsAgo, lastDayOfMonth } from '@/utils/dateFormat';

type ButtonType = 'button' | 'submit' | 'reset';
type ButtonTheme = 'primary' | 'secondary' | 'text' | 'tertiary';


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseYmdForm',
  props: {
  type: {
    type: String as PropType<ButtonType>,
    default: 'button',
  },
  theme: {
    type: String as PropType<ButtonTheme>,
    default: 'primary',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: String, // 'yyyy-MM-dd'
    default: '',
  },
  minYmd: {
    type: String, // 'yyyy-MM-dd'
    default: '',
  },
  maxYmd: {
    type: String, // 'yyyy-MM-dd'
    default: '',
  },
  suffixYearText: {
    type: String,
    default: '年',
  },
  suffixMonthText: {
    type: String,
    default: '月',
  },
  suffixDayText: {
    type: String,
    default: '日',
  },

  placeholder: {
    type: String,
    default: '',
  },
  yearSort: {
    type: String as PropType<'asc' | 'desc'>,
    default: 'asc',
  },
  hasCancelButton: {
    type: Boolean,
    default: false,
  },
},
  emits: ["update:modelValue"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:modelValue', value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props



const yPlaceholder = props.placeholder.split('-')[0];
const mPlaceholder = String(Number(props.placeholder.split('-')[1]));
const dPlaceholder = String(Number(props.placeholder.split('-')[2]));

const propMinYear = computed(() => {
  if (!props.minYmd) return '';
  return props.minYmd.split('-')[0];
});

const propMaxYear = computed(() => {
  if (!props.maxYmd) return '';
  return props.maxYmd.split('-')[0];
});

const minYear = computed(() => propMinYear.value || '1945');
const maxYear = computed(() => propMaxYear.value || fifteenYearsAgo());

const yOptions: Ref<Options> = computed(() => {
  const opts: Options = [];
  for (let i = Number(minYear.value); i <= Number(maxYear.value); i++) {
    opts.push({ label: String(i), value: String(i) });
  }
  if (props.yearSort === 'desc') {
    opts.sort(function (a, b) {
      if (Number(a.value) > Number(b.value)) return -1;
      return 1;
    });
  }
  return opts;
});

const { modelValue } = toRefs(props);

const y = ref(modelValue.value.split('-')[0] || '');
const m = ref(modelValue.value.split('-')[1] || '');
const d = ref(modelValue.value.split('-')[2] || '');

watch(modelValue, () => {
  y.value = modelValue.value.split('-')[0] || '';
  m.value = modelValue.value.split('-')[1] || '';
  d.value = modelValue.value.split('-')[2] || '';
});

const minMonth = computed(() => {
  if (props.minYmd && propMinYear.value === y.value) {
    return props.minYmd.split('-')[1];
  }
  return '01';
});

const maxMonth = computed(() => {
  if (props.maxYmd && propMaxYear.value === y.value) {
    return props.maxYmd.split('-')[1];
  }
  return '12';
});

const mOptions: Ref<Options> = computed(() => {
  const opts: Options = [];
  for (let i = Number(minMonth.value); i <= Number(maxMonth.value); i++) {
    const strI = i.toString().padStart(2, '0');
    opts.push({ label: String(i), value: strI });
  }
  return opts;
});

const selectedStartOfMonth: Ref<Date> = computed(() => {
  return new Date(`${y.value}-${m.value}-01`);
});

const minDay = computed(() => {
  if (props.minYmd && propMinYear.value === y.value && minMonth.value === m.value) {
    return props.minYmd.split('-')[2];
  }
  return '01';
});

const maxDay = computed(() => {
  if (props.maxYmd && propMaxYear.value === y.value && maxMonth.value === m.value) {
    return props.maxYmd.split('-')[2];
  }
  if (y.value && m.value) {
    return lastDayOfMonth(selectedStartOfMonth.value);
  }
  return '31';
});

const dOptions: Ref<Options> = computed(() => {
  const opts: Options = [];
  for (let i = Number(minDay.value); i <= Number(maxDay.value); i++) {
    const strI = i.toString().padStart(2, '0');
    opts.push({ label: String(i), value: strI });
  }
  return opts;
});



watchEffect(() => {
  if (y.value && (Number(minMonth.value) > Number(m.value) || Number(maxMonth.value) < Number(m.value))) {
    m.value = '';
    d.value = '';
  } else if (y.value && m.value && !some(dOptions.value, { label: String(Number(d.value)), value: d.value })) {
    d.value = '';
  }
  if (y.value && m.value && d.value) {
    emit('update:modelValue', `${y.value}-${m.value}-${d.value}`);
  } else if (y.value && m.value) {
    emit('update:modelValue', `${y.value}-${m.value}-`);
  } else if (y.value) {
    emit('update:modelValue', `${y.value}--`);
  } else {
    emit('update:modelValue', '');
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(BaseSelectBox, {
          options: _unref(yOptions),
          modelValue: y.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((y).value = $event)),
          placeholder: _unref(yPlaceholder),
          hasCancelButton: props.hasCancelButton
        }, null, 8, ["options", "modelValue", "placeholder", "hasCancelButton"])
      ]),
      _createElementVNode("span", _hoisted_4, _toDisplayString(__props.suffixYearText), 1)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createVNode(BaseSelectBox, {
          options: _unref(mOptions),
          modelValue: m.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((m).value = $event)),
          placeholder: _unref(mPlaceholder),
          hasCancelButton: props.hasCancelButton
        }, null, 8, ["options", "modelValue", "placeholder", "hasCancelButton"])
      ]),
      _createElementVNode("span", _hoisted_7, _toDisplayString(__props.suffixMonthText), 1)
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("div", _hoisted_9, [
        _createVNode(BaseSelectBox, {
          options: _unref(dOptions),
          modelValue: d.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((d).value = $event)),
          placeholder: _unref(dPlaceholder),
          hasCancelButton: props.hasCancelButton
        }, null, 8, ["options", "modelValue", "placeholder", "hasCancelButton"])
      ]),
      _createElementVNode("span", _hoisted_10, _toDisplayString(__props.suffixDayText), 1)
    ])
  ]))
}
}

})